import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../lib/api';
import api from '../lib/api';

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  app_name: 'WorkStream Pro',
  logo_url: '',
  logo_text: '',
  primary_color: '#F97316',
  secondary_color: '#EAB308',
  accent_color: '#22C55E',
  background_color: '#09090B',
  card_color: '#18181B',
  text_color: '#FAFAFA',
  font_family: 'Barlow Condensed',
  dark_mode: true,
  show_logo_text: true,
  custom_css: '',
  // Feature flags - default to true
  feature_cost_tracking: true,
  feature_documents: true,
  feature_technician_dashboard: true,
  feature_custom_fields: true,
  feature_milestones: true,
  feature_before_photos: true
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [companyBranding, setCompanyBranding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  useEffect(() => {
    // Apply CSS variables when branding changes
    const effectiveBranding = companyBranding 
      ? { ...branding, primary_color: companyBranding.primary_color || branding.primary_color, secondary_color: companyBranding.secondary_color || branding.secondary_color }
      : branding;
    
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', effectiveBranding.primary_color);
    root.style.setProperty('--brand-secondary', effectiveBranding.secondary_color);
    root.style.setProperty('--brand-accent', effectiveBranding.accent_color);
    root.style.setProperty('--brand-bg', effectiveBranding.background_color);
    root.style.setProperty('--brand-card', effectiveBranding.card_color);
    root.style.setProperty('--brand-text', effectiveBranding.text_color);
    
    // Apply custom CSS if exists
    let customStyleEl = document.getElementById('custom-branding-css');
    if (effectiveBranding.custom_css) {
      if (!customStyleEl) {
        customStyleEl = document.createElement('style');
        customStyleEl.id = 'custom-branding-css';
        document.head.appendChild(customStyleEl);
      }
      customStyleEl.textContent = effectiveBranding.custom_css;
    } else if (customStyleEl) {
      customStyleEl.remove();
    }
  }, [branding, companyBranding]);

  const fetchBranding = async () => {
    try {
      const response = await settingsAPI.getBranding();
      setBranding({ ...DEFAULT_BRANDING, ...response.data });
    } catch (err) {
      console.error('Failed to fetch branding:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateBranding = async (newBranding) => {
    try {
      const response = await settingsAPI.updateBranding(newBranding);
      setBranding(response.data);
      return { success: true };
    } catch (err) {
      console.error('Failed to update branding:', err);
      return { success: false, error: err.response?.data?.detail || 'Failed to save' };
    }
  };

  const resetBranding = () => {
    setBranding(DEFAULT_BRANDING);
    setCompanyBranding(null);
  };

  // Set company-specific branding (called after login)
  const setCompanyContext = useCallback((company) => {
    if (company) {
      setCompanyBranding({
        id: company.id,
        name: company.name,
        slug: company.slug,
        logo_url: company.logo_url,
        primary_color: company.primary_color,
        secondary_color: company.secondary_color,
        // Include feature flags if present
        feature_custom_fields: company.feature_custom_fields,
        feature_cost_tracking: company.feature_cost_tracking,
        feature_documents: company.feature_documents,
        feature_technician_dashboard: company.feature_technician_dashboard,
        feature_milestones: company.feature_milestones,
        feature_before_photos: company.feature_before_photos
      });
      // Re-fetch branding to get company-specific settings
      fetchBranding();
    } else {
      setCompanyBranding(null);
    }
  }, []);

  // Fetch company branding by slug (for login page)
  const fetchCompanyBySlug = useCallback(async (slug) => {
    try {
      const response = await api.get(`/companies/by-slug/${slug}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch company by slug:', err);
      return null;
    }
  }, []);

  // Get effective branding (company branding takes precedence for certain fields)
  const getEffectiveBranding = useCallback(() => {
    if (!companyBranding) return branding;
    return {
      ...branding,
      app_name: companyBranding.name || branding.app_name,
      logo_url: companyBranding.logo_url || branding.logo_url,
      primary_color: companyBranding.primary_color || branding.primary_color,
      secondary_color: companyBranding.secondary_color || branding.secondary_color,
      // Feature flags - company branding overrides if explicitly set
      feature_custom_fields: companyBranding.feature_custom_fields !== undefined 
        ? companyBranding.feature_custom_fields 
        : branding.feature_custom_fields,
      feature_cost_tracking: companyBranding.feature_cost_tracking !== undefined 
        ? companyBranding.feature_cost_tracking 
        : branding.feature_cost_tracking,
      feature_documents: companyBranding.feature_documents !== undefined 
        ? companyBranding.feature_documents 
        : branding.feature_documents,
      feature_technician_dashboard: companyBranding.feature_technician_dashboard !== undefined 
        ? companyBranding.feature_technician_dashboard 
        : branding.feature_technician_dashboard,
      feature_milestones: companyBranding.feature_milestones !== undefined 
        ? companyBranding.feature_milestones 
        : branding.feature_milestones,
      feature_before_photos: companyBranding.feature_before_photos !== undefined 
        ? companyBranding.feature_before_photos 
        : branding.feature_before_photos
    };
  }, [branding, companyBranding]);

  return (
    <BrandingContext.Provider value={{ 
      branding: getEffectiveBranding(), 
      companyBranding,
      loading, 
      updateBranding, 
      resetBranding,
      refetchBranding: fetchBranding,
      setCompanyContext,
      fetchCompanyBySlug
    }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;
